import {
  IconArrowUp,
  IconCoins,
  IconMovie,
  IconResize,
  IconUsersGroup,
} from '@tabler/icons-react';
import type { MouseEvent } from 'react';
import { RelatedGrokTools } from '@/components/custom/related-grok-tools';
import {
  ContentSection,
  DataTable,
} from '@/components/landing/landing-sections';
import { DashboardHeader } from '@/components/layout/dashboard-header';
import {
  HomeHeaderActions,
  HomeHeaderNav,
} from '@/components/layout/home-header';
import { OneTimePricingCards } from '@/components/pricing/one-time-pricing-cards';
import { TrendWorkbench } from '@/components/trend/trend-workbench';
import { TRENDS } from '@/config/trends';
import { localizeHref } from '@/lib/locale';
import { Routes } from '@/lib/routes';
import { getRamalamaCopy } from './ramalama-copy';
import {
  RAMALAMA_LAST_UPDATED,
  RAMALAMA_STORYBOARD_IMAGE,
  RamalamaJsonLd,
} from './ramalama-json-ld';
import { TikTokEmbeds } from './tiktok-embeds';

export const RAMALAMA_WORKBENCH_ID = 'ramalama-trend-workbench';

/** Same order as `copy.features.items`. */
const FEATURE_ICONS = [IconMovie, IconUsersGroup, IconResize, IconCoins];

/** Scroll to the workbench and focus its photo slot. */
function goToWorkbench(event: MouseEvent<HTMLAnchorElement>) {
  const workbench = document.getElementById(RAMALAMA_WORKBENCH_ID);
  if (!workbench) return;
  event.preventDefault();
  workbench.scrollIntoView({ behavior: 'smooth', block: 'start' });
  workbench
    .querySelector<HTMLElement>('[data-trend-photo-slot]')
    ?.focus({ preventScroll: true });
}

export function RamalamaPage() {
  const copy = getRamalamaCopy();
  const titleLead = copy.title.endsWith(copy.titleAccent)
    ? copy.title.slice(0, -copy.titleAccent.length).trimEnd()
    : copy.title;
  const titleAccent = titleLead === copy.title ? '' : copy.titleAccent;

  return (
    <div className="dark flex min-h-svh flex-col bg-[#090909] text-white">
      <RamalamaJsonLd />
      <DashboardHeader
        actions={<HomeHeaderActions />}
        center={<HomeHeaderNav />}
        showCredits={false}
        showLocale={false}
        showMode={false}
        showSidebarTrigger={false}
      />

      <main className="flex-1">
        <header className="mx-auto max-w-4xl px-4 pt-6 pb-4 text-center sm:pt-8 lg:px-6">
          <nav
            aria-label="Breadcrumb"
            className="mb-4 flex items-center justify-center gap-2 text-sm text-white/50"
          >
            <a
              href={localizeHref(Routes.Root)}
              className="transition hover:text-white/80"
            >
              {copy.breadcrumbHome}
            </a>
            <span aria-hidden>/</span>
            <span aria-current="page" className="text-white/75">
              {copy.breadcrumbCurrent}
            </span>
          </nav>
          <h1 className="font-bold font-sans text-4xl leading-tight tracking-tight sm:text-5xl">
            {titleAccent ? `${titleLead} ` : titleLead}
            {titleAccent ? (
              <span className="text-[#7ee35f]">{titleAccent}</span>
            ) : null}
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-base text-white/72 leading-7 sm:text-lg">
            {copy.subtitle}
          </p>
        </header>

        <div id={RAMALAMA_WORKBENCH_ID} className="scroll-mt-20">
          <TrendWorkbench trend={TRENDS.ramalama} />
        </div>

        <RamalamaAnswerContent />
      </main>
    </div>
  );
}

function RamalamaAnswerContent() {
  const copy = getRamalamaCopy();

  return (
    <div
      className="border-white/10 border-t px-4 py-12 lg:px-6"
      data-keyword-supporting-content
      data-section="keyword-supporting-content"
    >
      <div className="mx-auto max-w-5xl space-y-14">
        <section
          className="space-y-6"
          data-keyword-module="quick-answer"
          data-section="quick-answer"
          aria-label="Quick answer"
        >
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1.4fr)_minmax(16rem,0.6fr)]">
            <p className="rounded-lg border border-[#7ee35f]/25 bg-[#0f1a0c] p-5 text-base text-white/85 leading-7 sm:p-6 sm:text-lg sm:leading-8">
              {copy.quickAnswer}
            </p>
            <dl className="space-y-3 rounded-lg border border-white/10 bg-white/[0.03] p-5 text-sm">
              {copy.quickFacts.map((fact) => (
                <div key={fact.label}>
                  <dt className="text-white/55">{fact.label}</dt>
                  <dd className="mt-0.5 text-white/85">{fact.value}</dd>
                </div>
              ))}
            </dl>
          </div>
          <figure className="rounded-lg border border-white/10 bg-white/[0.02] p-4 sm:p-5">
            <img
              src={RAMALAMA_STORYBOARD_IMAGE}
              alt={copy.storyboard.label}
              width={960}
              height={573}
              loading="lazy"
              decoding="async"
              className="h-auto w-full rounded-md"
            />
            <figcaption className="mt-4 grid gap-2 text-sm text-white/68 leading-6 sm:grid-cols-3">
              {copy.storyboard.captions.map((caption) => (
                <span key={caption}>{caption}</span>
              ))}
            </figcaption>
          </figure>
        </section>

        <ContentSection id="demos" moduleName="demos" title={copy.demos.title}>
          <p className="text-white/72 leading-7">{copy.demos.intro}</p>
          <TikTokEmbeds items={copy.demos.items} />
          <p className="text-white/50 text-xs leading-5">{copy.demos.note}</p>
        </ContentSection>

        <ContentSection
          id="comparison"
          moduleName="comparison"
          title={copy.comparison.title}
        >
          <p className="text-white/72 leading-7">{copy.comparison.intro}</p>
          <DataTable
            columns={copy.comparison.columns}
            rows={copy.comparison.rows}
          />
        </ContentSection>

        <ContentSection id="pain" moduleName="pain" title={copy.pain.title}>
          {copy.pain.paragraphs.map((paragraph) => (
            <p key={paragraph} className="text-white/72 leading-7">
              {paragraph}
            </p>
          ))}
        </ContentSection>

        <ContentSection
          id="why-choose"
          moduleName="core-capabilities"
          title={copy.features.title}
        >
          <p className="text-white/72 leading-7">{copy.features.intro}</p>
          <ul className="grid gap-4 sm:grid-cols-2">
            {copy.features.items.map((feature, index) => {
              const Icon = FEATURE_ICONS[index] ?? IconMovie;
              return (
                <li
                  key={feature.title}
                  className="rounded-lg border border-white/10 bg-white/[0.03] p-5"
                >
                  <Icon className="size-5 text-[#7ee35f]" aria-hidden />
                  <h3 className="mt-3 font-semibold text-lg">
                    {feature.title}
                  </h3>
                  <p className="mt-2 text-sm text-white/68 leading-6">
                    {feature.body}
                  </p>
                </li>
              );
            })}
          </ul>
        </ContentSection>

        <ContentSection
          id="advantage"
          moduleName="advantage"
          title={copy.advantage.title}
        >
          <ul className="grid gap-4 sm:grid-cols-2">
            {copy.advantage.items.map((item) => (
              <li
                key={item.title}
                className="rounded-lg border border-white/10 bg-white/[0.03] p-5"
              >
                <h3 className="font-semibold text-lg">{item.title}</h3>
                <p className="mt-2 text-sm text-white/68 leading-6">
                  {item.body}
                </p>
              </li>
            ))}
          </ul>
        </ContentSection>

        <ContentSection id="input" moduleName="input" title={copy.input.title}>
          <p className="text-white/72 leading-7">{copy.input.intro}</p>
          <ul className="list-disc space-y-2 pl-5 text-white/72 leading-7 marker:text-[#7ee35f]">
            {copy.input.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </ContentSection>

        <ContentSection
          id="how-it-works"
          moduleName="workflow"
          title={copy.steps.title}
        >
          <p className="text-white/72 leading-7">{copy.steps.intro}</p>
          <ol className="grid gap-3 md:grid-cols-5">
            {copy.steps.items.map((step, index) => (
              <li
                key={step.name}
                className="relative rounded-lg border border-white/10 bg-white/[0.03] p-4"
              >
                <span className="inline-flex size-8 items-center justify-center rounded-full bg-[#7ee35f] font-bold text-[#0b1208] text-sm">
                  {index + 1}
                </span>
                <h3 className="mt-3 font-semibold">{step.name}</h3>
                <p className="mt-1 text-sm text-white/68 leading-6">
                  {step.text}
                </p>
                {index < copy.steps.items.length - 1 ? (
                  <span
                    aria-hidden
                    className="-right-2.5 -translate-y-1/2 absolute top-1/2 hidden text-[#7ee35f] md:block"
                  >
                    →
                  </span>
                ) : null}
              </li>
            ))}
          </ol>
        </ContentSection>

        <ContentSection
          id="use-cases"
          moduleName="use-cases"
          title={copy.useCases.title}
        >
          <DataTable
            columns={copy.useCases.columns}
            rows={copy.useCases.rows}
          />
        </ContentSection>

        <ContentSection
          id="which-tool"
          moduleName="decision"
          title={copy.decision.title}
        >
          <div className="overflow-x-auto rounded-lg border border-white/10">
            <table className="w-full min-w-[36rem] text-left text-sm">
              <thead className="bg-white/[0.05] text-white/85">
                <tr>
                  {copy.decision.columns.map((column) => (
                    <th key={column} scope="col" className="px-4 py-3">
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 text-white/72">
                {copy.decision.rows.map((row) => (
                  <tr key={row.goal}>
                    <td className="px-4 py-3">{row.goal}</td>
                    <td className="px-4 py-3 font-semibold text-white">
                      {typeof row.tool === 'string' ? (
                        row.tool
                      ) : (
                        <a
                          href={localizeHref(row.tool.href)}
                          className="text-[#7ee35f] underline-offset-4 hover:underline"
                        >
                          {row.tool.label}
                        </a>
                      )}
                    </td>
                    <td className="px-4 py-3">{row.why}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ContentSection>

        <ContentSection
          id="file-handling"
          moduleName="file-handling"
          title={copy.fileHandling.title}
        >
          <ul className="list-disc space-y-2 pl-5 text-white/72 leading-7 marker:text-[#7ee35f]">
            {copy.fileHandling.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <a
            href={localizeHref(copy.fileHandling.pricingLink.href)}
            className="inline-block font-semibold text-[#7ee35f] text-sm underline-offset-4 hover:underline"
          >
            {copy.fileHandling.pricingLink.label}
          </a>
        </ContentSection>

        <section
          id="pricing"
          data-section="pricing"
          aria-labelledby="ramalama-pricing-title"
          className="scroll-mt-20"
        >
          <h2
            id="ramalama-pricing-title"
            className="font-semibold text-2xl sm:text-3xl"
          >
            {copy.pricing.title}
          </h2>
          <p className="mt-3 max-w-2xl text-white/72 leading-7">
            {copy.pricing.intro}
          </p>
          <div className="mt-8">
            <OneTimePricingCards source="ramalama-trend" />
          </div>
        </section>

        <ContentSection id="faq" moduleName="faq" title={copy.faqTitle}>
          <div className="divide-y divide-white/10 rounded-lg border border-white/10 bg-white/[0.03]">
            {copy.faqs.map((faq) => (
              <div key={faq.question} className="px-5 py-4">
                <h3 className="font-semibold">{faq.question}</h3>
                <p className="mt-2 text-sm text-white/72 leading-6">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </ContentSection>

        <div data-keyword-module="related-tools" data-section="related-tools">
          <RelatedGrokTools currentPath={Routes.RamalamaTrend} />
        </div>

        <section
          id="cta"
          data-keyword-module="cta"
          data-section="cta"
          aria-labelledby="cta-title"
          className="rounded-lg border border-[#7ee35f]/25 bg-[#0f1a0c] p-6 text-center sm:p-8"
        >
          <h2
            id="cta-title"
            className="font-serif text-2xl tracking-normal sm:text-3xl"
          >
            {copy.cta.title}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-white/72 leading-7">
            {copy.cta.body}
          </p>
          <a
            href={`#${RAMALAMA_WORKBENCH_ID}`}
            onClick={goToWorkbench}
            className="mt-5 inline-flex h-11 items-center gap-2 rounded-md bg-[#7ee35f] px-5 font-semibold text-[#0b1208] text-sm transition hover:opacity-90"
          >
            {copy.cta.button}
            <IconArrowUp className="size-4" aria-hidden />
          </a>
        </section>

        <footer
          className="space-y-1 text-center text-white/50 text-xs leading-5"
          data-section="disclaimer"
        >
          <p>{copy.disclaimer}</p>
          <p>
            {copy.lastUpdatedLabel}:{' '}
            <time dateTime={RAMALAMA_LAST_UPDATED}>
              {RAMALAMA_LAST_UPDATED}
            </time>
          </p>
        </footer>
      </div>
    </div>
  );
}
